/**
 * The body of the glossary page (`/metal-resources/references/glossary/`): a hero with a search
 * box, letter-range filters (A - C … V - Z), a contact panel (Contact Us and Live Chat buttons),
 * and every term with its rich-text definition, grouped by the first letter of its title. Desktop
 * shows the filters beside the list; mobile shows one accordion tab per letter range.
 *
 * Position:
 * - Rendered by `src/pages/metal-resources/references/glossary/index.tsx`, which passes the
 *   current language's terms (from `src/contentful/glossary/getContentfulGlossary.ts`, already
 *   filtered to the brand and ordered by title), the copy and the brand name.
 * - `MakeHighlights` is also the highlighter for the FAQ page (`src/components/FAQ/FAQ.tsx`) and
 *   for search matches inside rich text (`src/components/Shared/model/Contentful.tsx` →
 *   `renderRichText`). `GlossaryPageSetup` is the FAQ page's label type too.
 * - `src/components/FAQ/FAQ.tsx` is a near copy of this component (same layout, same search and
 *   filter logic); a change to one usually belongs in the other.
 *
 * Search (all in the browser, over the terms already on the page):
 * - Search text of one or two characters shows every term; from three characters, a term
 *   matches when its title contains the search text, or when the first text node of its
 *   definition does. Text later in a definition (a second paragraph, or after a bold or linked
 *   run) is not searched. Matches in titles and definitions are highlighted.
 * - Choosing a letter range clears the search, and typing clears the letter range.
 * - Desktop passes the typed text through `src/components/Utilities/Utilities.tsx` →
 *   `sanitizeInput` before storing it; mobile stores it as typed. The stored text goes to
 *   `renderRichText` as its highlight pattern.
 *
 * A term whose title doesn't start with a letter from a to z (a digit, an accented letter) is in
 * no letter range, so it is never shown.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import styled from "@emotion/styled";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { Button } from "@ryerson/frontend.button";
import { renderRichText } from "@components/Shared/model/Contentful";
import { Breadcrumb, BreadcrumbProps, Crumb } from "@ryerson/frontend.navigation";
import { ContentSection } from "@ryerson/frontend.layout";
import { SearchInput } from "@ryerson/frontend.form-controls";
import { Media } from "@ryerson/frontend.next";
import { Accordion, AccordionTab } from "@ryerson/frontend.expansion";
import { Icon } from "@ryerson/frontend.assets";
import { navigate } from "@ryerson/frontend.next";
import { ProcessedContentfulGlossaryFields } from "@contentful/glossary/types";
import { sanitizeInput } from "../../Utilities/Utilities";
import Spacer from "@components/Shared/Spacer/Spacer";

/**
 * One language's hero copy. Held in
 * `src/content/shared/metal-resources/references/glossary/index.ts` → `GlossaryHeroContent`.
 */
export type GlossaryHero = {
	title: string;
	searchPlaceholder: string;
	description: string;
};

/**
 * One language's contact-panel and search-result labels, shared by the glossary and FAQ pages.
 * Held in `src/pages/metal-resources/references/glossary/index.tsx` → `GlossaryPageContent`.
 */
export type GlossaryPageSetup = {
	/** Contact panel heading. */
	contactLabel: string;
	/** Label of the button that goes to `/contact-us`. */
	contactButton: string;
	/**
	 * Label of the button that maximises the live-chat widget (`window.LiveChatWidget`); the
	 * button does nothing while the widget isn't loaded.
	 */
	contactChat: string;
	/** Follows the result count in the desktop results bar: `<n> search results for "<text>"`. */
	searchResults: string;
	/** Label of the desktop results bar's clear button. FAQ doesn't show the bar. */
	clearResults: string;
};

/** Props for {@link GlossaryPage}. */
export type GlossaryPageProps = {
	content: GlossaryPageSetup;
	hero: GlossaryHero;
	/** The current language's terms, in display order. */
	queryData: ProcessedContentfulGlossaryFields[];
	/**
	 * `"Ryerson"` shows the shared `Breadcrumb`, which builds itself from the URL; any other value
	 * shows the fixed breadcrumb `GlossaryCustomBreadcrumb`.
	 */
	company: string;
};

// The letter-range filters. A term belongs to the range whose `query` holds the lower-cased first
// character of its title.
const GlossaryGroups = [
	{
		label: "A - C",
		query: ["a", "b", "c"],
	},
	{
		label: "D - F",
		query: ["d", "e", "f"],
	},
	{
		label: "G - I",
		query: ["g", "h", "i"],
	},
	{
		label: "J - L",
		query: ["j", "k", "l"],
	},
	{
		label: "M - O",
		query: ["m", "n", "o"],
	},
	{
		label: "P - R",
		query: ["p", "q", "r"],
	},
	{
		label: "S - U",
		query: ["s", "t", "u"],
	},
	{
		label: "V - Z",
		query: ["v", "w", "x", "y", "z"],
	},
];

// The non-Ryerson breadcrumb. Its last crumb's URL has a capital `G`.
const GlossaryCustomBreadcrumb: Crumb[] = [
	{ label: "Home", url: "/" },
	{ label: "Metal Resources", url: "/metal-resources" },
	{ label: "Glossary", url: "/metal-resources/references/Glossary" },
];

const GlossaryBreadcrumb: React.FC = () => {
	const GlossaryBreadcrumb: BreadcrumbProps = {
		data: GlossaryCustomBreadcrumb,
		type: "tertiary",
		size: "xs",
	};
	return <Breadcrumb {...GlossaryBreadcrumb} />;
};

const Left = styled.div`
	display: inline-block;
	width: 260px;
	height: auto;
	vertical-align: top;
`;

const ContactPanel = styled.div`
	width: 100%;
	height: auto;
	padding: 20px 20px 25px 20px;
	margin-top: 10px;
	border-radius: 2px;
`;

const Filters = styled.div`
	display: block;
	width: 100%;
	height: auto;
	border-radius: 2px;
`;

const Filter = styled.div`
	display: block;
	width: 100%;
	height: 40px;
	padding-left: 20px;
	box-sizing: border-box;
	line-height: 40px;
	cursor: pointer;
`;

const Right = styled.div`
	display: inline-block;
	width: calc(100% - 270px);
	height: auto;
	margin-left: 10px;
	padding: 0px;
	border-radius: 2px;
`;

const RightContent = styled.div`
	display: block;
	width: 100%;
	height: auto;
	padding: 40px;
	box-sizing: border-box;
`;

const EndDivider = styled.hr`
	display: block;
	width: 100%;
	margin-bottom: 100px;
	margin-top: 0px;
	opacity: 0.1;
`;

const BeginningDivider = styled.hr`
	display: block;
	width: 100%;
	margin-top: 0;
	margin-bottom: 0;
	opacity: 0.1;
`;

const GlossaryItem = styled.div`
	display: block;
	width: 100%;
	height: auto;
	padding-top: 25px;
	padding-bottom: 25px;
`;


const AccordionContainer = styled.div`
	display: block;
	width: 100%;
	padding: 20px;
	box-sizing: border-box;
	height: auto;
`;

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const HeroLeft = styled.div`
	display: inline-block;
	vertical-align: top;
	width: calc(100% - 320px);
	height: auto;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				height: auto;
			}
		`;
	}}
`;

const HeroRight = styled.div`
	display: inline-block;
	vertical-align: top;
	width: 320px;
	padding-top: 15px;
	height: auto;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				height: auto;
			}
		`;
	}}
`;

const SearchResults = styled.div`
	display: block;
	height: 0px;
	width: 100%;
	overflow: hidden;
	white-space: nowrap;
`;

const SearchResultsText = styled.div`
	display: inline-block;
	vertical-align: top;
	width: 215px;
	height: 20px;
	margin-top: 20px;
	padding: 0 31px 0 20px;
	box-sizing: border-box;
`;

const SearchResultsClear = styled.div`
	display: inline-block;
	vertical-align: top;
	line-height: 60px;
	width: 180px;
	padding: 0 0 0 30px;
`;

const SearchClearIcon = styled.div`
	display: inline-block;
	width: 24px;
	height: 24px;
	border-radius: 24px;
	text-align: center;
	line-height: 24px;
	box-sizing: border-box;
	margin-right: 12px;
`;

const ClearSearch = styled.div`
	display: inline-block;
	width: auto;
	height: auto;
	cursor: pointer;
`;

/**
 * Turns a string with `|text|` markers into React nodes, drawing each marked run on the theme's
 * light-yellow background. A string with no marker is returned unchanged.
 *
 * Callers wrap each match of the search text in `|…|` before calling: the title search here and
 * in `src/components/FAQ/FAQ.tsx`, and `src/components/Shared/model/Contentful.tsx` →
 * `renderRichText` for paragraphs. Only markers whose content is ASCII word characters (`\w`:
 * A–Z, a–z, 0–9, `_`) are recognised, so a match containing a space, a hyphen or an accented
 * letter (French "matériau", Spanish "aleación") isn't highlighted.
 *
 * When none of a string's markers is recognised, the string is returned as it is, `|` markers
 * included, so the pipes show on the page. When at least one marker is recognised, every `|` is
 * removed, and the text of any unrecognised marker is drawn without a highlight.
 *
 * Calls `useTheme`, so it must only be called while a component renders.
 */
export const MakeHighlights = (highlightString: any) => {
	const uniqueArrayItems = (value: any, index: any, self: any) => {
		return self.indexOf(value) === index;
	};
	const { theme } = useTheme();
	let matches = highlightString.match(/\|\w*\|/g);
	if (matches && matches.length > 0) {
		matches = matches.filter(uniqueArrayItems);
		let newString = highlightString;
		let parts = newString.split("|");
		let out: any[] = [];
		parts.forEach((p: any, index: number) => {
			let foundHighlight = false;
			for (let i = 0; i < matches.length; i++) {
				if (p === matches[i].replaceAll("|", "")) {
					foundHighlight = true;
					out.push(
						<span
							key={index}
							css={css`
								background-color: ${theme.colors.secondary.lightYellow};
							`}
						>
							{p}
						</span>
					);
				}
			}
			if (!foundHighlight) out.push(<React.Fragment key={index}>{p}</React.Fragment>);
		});

		return <>{out}</>;
	} else {
		return highlightString;
	}
};

/**
 * Renders the glossary: hero and search, letter-range filters, contact panel and the grouped
 * terms. See the file header for how search and the filters behave.
 */
export const GlossaryPage: React.FC<GlossaryPageProps> = ({
	content,
	hero,
	company,
	queryData,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const [currentGroup, setCurrentGroup] = React.useState<string>("");
	const [currentItems, setCurrentItems] =
		React.useState<(ProcessedContentfulGlossaryFields & { highlightedTitle?: string })[]>(
			queryData
		);
	const [currentGroups, setCurrentGroups] = React.useState<any[]>(GlossaryGroups);
	const [nSearchResults, setNSearchResults] = React.useState<number>(0);
	// Letter-range filter. Choosing a range clears the search and keeps only that range's terms.
	// Clearing the range, or a language change while no range is chosen, restores every term and
	// range. A language change while a range is chosen applies that range again to the new
	// language's terms.
	React.useEffect(() => {
		if (currentGroup.length > 0) {
			setSearchTerm("");
			let newItems: ProcessedContentfulGlossaryFields[] = [];
			let newGroups: any[] = [];
			let query: any[];
			GlossaryGroups.forEach((group: any) => {
				if (group.label === currentGroup) {
					query = group.query;
					newGroups.push(group);
				}
			});
			queryData.forEach((item) => {
				if (query.includes(item.title.charAt(0).toLowerCase())) newItems.push(item);
			});
			setCurrentGroups(newGroups);
			setCurrentItems(newItems);
		} else {
			setCurrentItems(queryData);
			setCurrentGroups(GlossaryGroups);
		}
	}, [currentGroup, language]);

	const [searchTerm, setSearchTerm] = React.useState<string>("");

	/**
	 * Whether the first text node of a rich-text document contains `term`, case-insensitively. It
	 * follows only the first child at each level and returns at the first leaf, so later
	 * paragraphs and later runs of the same paragraph are never checked.
	 */
	const recursiveLook = (obj: any, term: string): boolean => {
		if (obj.content) {
			if (obj.content.length > 0) {
				for (let i = 0; i < obj.content.length; i++) {
					let child = obj.content[i];
					if (child.content) {
						return recursiveLook(child, term);
					} else if (child.value && child.value.length > 0) {
						return child.value.toLowerCase().includes(term.toLowerCase());
					} else {
						return false;
					}
				}
			} else {
				return false;
			}
		} else {
			return false;
		}
		return false;
	};

	// Search. A title match is copied with `Object.create` and given `highlightedTitle`: the title
	// with every occurrence of the first match's text wrapped in `|…|` for `MakeHighlights`. A
	// definition match needs two tests to pass: the raw JSON string contains the text, then
	// `recursiveLook` finds it in the parsed document, so a hit on a JSON key such as `content`
	// doesn't count. The ranges shown are those that hold at least one match; no match empties
	// both the list and the ranges.
	React.useEffect(() => {
		const sanitizedSearchTerm = sanitizeInput(searchTerm, 50);

		if (searchTerm.length > 0) {
			setCurrentGroup("");
			setTimeout(() => {
				if (searchTerm.length > 2) {
					let filteredItems: ProcessedContentfulGlossaryFields[] = [];
					let filteredGroups: any[] = [];
					queryData.forEach((item) => {
						let mutableItem = Object.create(item);
						if (item.title.toLowerCase().includes(searchTerm.toLowerCase())) {
							if (
								mutableItem.title.toLowerCase().includes(searchTerm.toLowerCase())
							) {
								let re = new RegExp(sanitizedSearchTerm, "gi");
								let match = mutableItem.title.match(re);
								let newTitle = mutableItem.title;
								if (match && match.length > 0)
									newTitle = newTitle.replaceAll(match[0], "|" + match[0] + "|");
								mutableItem.highlightedTitle = newTitle;
							}
							filteredItems.push(mutableItem);
						} else {
							if (
								mutableItem.definition.raw
									.toLowerCase()
									.includes(searchTerm.toLowerCase())
							) {
								if (
									recursiveLook(
										JSON.parse(mutableItem.definition.raw),
										searchTerm
									)
								) {
									filteredItems.push(mutableItem);
								}
							}
						}
					});
					if (filteredItems.length > 0) {
						setNSearchResults(filteredItems.length);
						GlossaryGroups.forEach((group, index: number) => {
							let addToGroup = false;
							filteredItems.forEach((item, index: number) => {
								if (group.query.includes(item.title.charAt(0).toLowerCase())) {
									addToGroup = true;
								}
								if (index === filteredItems.length - 1) {
									if (addToGroup === true) {
										filteredGroups.push(group);
									}
								}
							});
							if (GlossaryGroups.length - 1 === index) {
								setCurrentGroups(filteredGroups);
								setCurrentItems(filteredItems);
							}
						});
					} else {
						setCurrentItems([]);
						setCurrentGroups([]);
						setNSearchResults(0);
					}
				} else {
					setCurrentItems(queryData);
					setCurrentGroups(GlossaryGroups);
					setNSearchResults(0);
				}
			}, 1);
		} else {
			setCurrentItems(queryData);
			setCurrentGroups(GlossaryGroups);
			setNSearchResults(0);
		}
	}, [searchTerm]);

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						{company === "Ryerson" ? (
							<Breadcrumb type="tertiary" size="xs" />
						) : (
							<GlossaryBreadcrumb />
						)}
					</BreadcrumbContainer>
					<HeroLeft theme={theme}>
						<Typography
							css={css`
								margin-top: 20px;
								margin-bottom: 25px;
							`}
							variant="h1"
							type="tertiary"
						>
							{hero.title}
						</Typography>
						<Typography variant="p" size="md" type="tertiary">
							{hero.description}
						</Typography>
					</HeroLeft>
					<HeroRight theme={theme}>
						<SearchInput
							shape="rounded"
							placeholder={hero.searchPlaceholder}
							size="md"
							label=""
							language={language}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(sanitizeInput(e.target.value, 50));
							}}
						/>
					</HeroRight>
				</ContentSection>
				<ContentSection type="tertiary" vPadding="0">
					<Left>
						<Filters
							css={css`
								background-color: ${theme.colors.secondary.background};
							`}
						>
							{GlossaryGroups.map((group: any, index: number) => {
								return (
									<Filter
										key={index}
										onClick={() => {
											group.label === currentGroup
												? setCurrentGroup("")
												: setCurrentGroup(group.label);
										}}
										css={css`
											background-color: ${group.label === currentGroup
												? theme.colors.primary.white
												: "transparent"};
										`}
									>
										<Typography
											color={
												group.label === currentGroup
													? theme.colors.primary.darkerGray
													: theme.colors.primary.link
											}
											variant="div"
											size="md"
											weight="bold"
											css={css`
												line-height: 40px;
											`}
										>
											{group.label}
										</Typography>
									</Filter>
								);
							})}
						</Filters>
						<ContactPanel
							css={css`
								background-color: ${theme.colors.primary.darkGray};
							`}
						>
							<Typography
								weight="bold"
								variant="h4"
								css={css`
									margin-top: 0px;
									margin-bottom: 30px;
									font-size: 20px;
									letter-spacing: -1px;
								`}
								type="tertiary"
							>
								{content.contactLabel}
							</Typography>
							<Button
								onClick={() => {
									navigate("/contact-us");
								}}
								size="sm"
								fullwidth={true}
								type="secondary"
								css={css`
									margin-bottom: 15px;
								`}
								shape="rounded"
								label={content.contactButton}
							/>
							<Button
								onClick={() => {
									if (window && (window as any).LiveChatWidget)
										(window as any).LiveChatWidget.call("maximize");
								}}
								size="sm"
								type="secondary"
								fullwidth={true}
								shape="rounded"
								label={content.contactChat}
							/>
						</ContactPanel>
					</Left>
					<Right
						css={css`
							background-color: ${theme.colors.primary.background};
						`}
					>
						<SearchResults
							css={css`
								background-color: ${theme.colors.primary.darkGray};
								height: ${nSearchResults > 0 ? "60px" : "0px"};
								transition: height 0.5s ease;
							`}
						>
							<SearchResultsText
								css={css`
									border-right: 1px solid rgba(255, 255, 255, 0.2);
								`}
							>
								<Typography
									variant="span"
									size="sm"
									color={theme.colors.primary.lightGray}
								>
									{nSearchResults + " " + content.searchResults}
									<Typography
										variant="span"
										size="sm"
										color={theme.colors.primary.white}
									>
										{" "}
										{'"' + searchTerm + '"'}
									</Typography>
								</Typography>
							</SearchResultsText>
							<SearchResultsClear>
								<ClearSearch
									onClick={() => {
										setSearchTerm("");
									}}
								>
									<SearchClearIcon
										css={css`
											border: 1px solid ${theme.colors.primary.gray};
										`}
									>
										<Icon
											size="xs"
											icon="close"
											css={css`
												cursor: pointer;
											`}
											color={theme.colors.primary.white}
										/>
									</SearchClearIcon>
									<Typography
										variant="span"
										color={theme.colors.primary.white}
										size="sm"
									>
										{content.clearResults}
									</Typography>
								</ClearSearch>
							</SearchResultsClear>
						</SearchResults>
						<RightContent>
							{currentGroups.map((group: any, index: number) => {
								return (
									<div
										key={index}
										css={css`
											display: ${currentGroup === group.label ||
											currentGroup === ""
												? "block"
												: "none"};
										`}
									>
										<Typography
											color={theme.colors.primary.darkGray}
											css={css`
												margin-top: 0px;
												margin-bottom: 40px;
											`}
											variant="h1"
										>
											{group.label}
										</Typography>
										<>
											{currentItems.map((item, index: number) => {
												return (
													<React.Fragment key={index}>
														{group.query.includes(
															item.title.charAt(0).toLowerCase()
														) ? (
															<>
																<BeginningDivider
																	css={{
																		color: theme.colors.primary
																			.darkGray,
																	}}
																/>
																<GlossaryItem>
																	<Typography
																		variant="h4"
																		type="primary"
																		css={css`
																			margin-top: 0px;
																			margin-bottom: 24px;
																		`}
																	>
																		{item.highlightedTitle &&
																		item.highlightedTitle
																			.length > 0
																			? MakeHighlights(
																					item.highlightedTitle
																			  )
																			: item.title}
																	</Typography>
																	<Typography
																		variant="div"
																		size="md"
																		type="primary"
																	>
																		{renderRichText(
																			item.definition.raw,
																			"primary",
																			null,
																			searchTerm
																		)}
																	</Typography>
																</GlossaryItem>
															</>
														) : (
															<></>
														)}
													</React.Fragment>
												);
											})}
										</>
										<EndDivider
											css={{
												color: theme.colors.primary.darkGray,
											}}
										/>
									</div>
								);
							})}
						</RightContent>
						<Spacer
							css={css`
								height: 1px;
								margin-top: -50px;
							`}
						/>
					</Right>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary">
					<BreadcrumbContainer theme={theme}>
						{company === "Ryerson" ? (
							<Breadcrumb type="tertiary" size="xs" />
						) : (
							<GlossaryBreadcrumb />
						)}
					</BreadcrumbContainer>
					<HeroLeft theme={theme}>
						<Typography
							css={css`
								margin-top: 20px;
								margin-bottom: 25px;
							`}
							variant="h1"
							type="tertiary"
						>
							{hero.title}
						</Typography>
						<Typography variant="p" size="md" type="tertiary">
							{hero.description}
						</Typography>
					</HeroLeft>
					<HeroRight theme={theme}>
						{/* Unlike the desktop box, this one stores the text unsanitised, and
						    `renderRichText` builds a regular expression from it. */}
						<SearchInput
							shape="rounded"
							placeholder={hero.searchPlaceholder}
							size="md"
							label=""
							language={language}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
							}}
						/>
					</HeroRight>
				</ContentSection>
				<ContentSection type="primary" hPadding="0px" vPadding="0px">
					<AccordionContainer>
						<Accordion type="primary" multiple={true}>
							{currentGroups.map((group: any, index: number) => {
								return (
									<AccordionTab
										key={index}
										circled={true}
										header={group.label}
										maxHeight="5000px"
										multiple={true}
										defaultOpen={false}
									>
										<Spacer
											css={css`
												height: 35px;
											`}
										/>
										<>
											{currentItems.map((item, index: number) => {
												return (
													<React.Fragment key={index}>
														{group.query.includes(
															item.title.charAt(0).toLowerCase()
														) ? (
															<>
																<Typography
																	variant="h5"
																	type="primary"
																	css={css`
																		margin-top: 0px;
																		margin-bottom: 13px;
																	`}
																>
																	{item.highlightedTitle &&
																	item.highlightedTitle.length > 0
																		? MakeHighlights(
																				item.highlightedTitle
																		  )
																		: item.title}
																</Typography>
																<Typography
																	variant="div"
																	size="md"
																	type="primary"
																	css={css`
																		margin-bottom: 25px;
																	`}
																>
																	{renderRichText(
																		item.definition.raw,
																		"primary",
																		null,
																		searchTerm
																	)}
																</Typography>
															</>
														) : (
															<></>
														)}
													</React.Fragment>
												);
											})}
										</>
									</AccordionTab>
								);
							})}
						</Accordion>
					</AccordionContainer>
					<ContactPanel
						css={css`
							background-color: ${theme.colors.primary.darkGray};
							display: block;
							width: 100%;
							margin-top: 30px;
							padding: 20px 20px 65px 20px;
						`}
					>
						<Typography
							weight="bold"
							variant="h4"
							css={css`
								margin-top: 0px;
								margin-bottom: 30px;
								font-size: 20px;
								letter-spacing: -1px;
							`}
							type="tertiary"
						>
							{content.contactLabel}
						</Typography>
						<Button
							onClick={() => {
								navigate("/contact-us");
							}}
							size="sm"
							fullwidth={true}
							type="secondary"
							css={css`
								margin-bottom: 15px;
							`}
							shape="rounded"
							label={content.contactButton}
						/>
						<Button
							onClick={() => {
								if (window && (window as any).LiveChatWidget)
									(window as any).LiveChatWidget.call("maximize");
							}}
							size="sm"
							type="secondary"
							fullwidth={true}
							shape="rounded"
							label={content.contactChat}
						/>
					</ContactPanel>
				</ContentSection>
			</Media>
		</>
	);
};

export default GlossaryPage;
